import React from 'react';
import { Typography, Grid, Card, CardContent, CardActionArea, Box } from '@mui/material';
import { useNavigate } from 'react-router';
import { useCampaignsQuery } from '../hooks/api';
import { StatusChip, LoadingIndicator, ErrorDisplay, CopyButton } from './common';
import { formatSecondsToHMS } from '../utils';

const Dashboard: React.FC = () => {
  const { data: campaigns = [], isLoading: loading, error, refetch } = useCampaignsQuery();
  const navigate = useNavigate();

  const handleCampaignClick = (id: string): void => {
    navigate(`/campaigns/${id}`);
  };

  if (loading) {
    return <LoadingIndicator message="Loading dashboard..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error.message || 'An error occurred while loading campaigns'}
        retryAction={() => refetch()}
        backRoute="/"
        backLabel="Refresh"
      />
    );
  }

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>
      <Box sx={{ mb: 4 }}>
        <Typography variant="body1">Total Campaigns: {campaigns.length}</Typography>
      </Box>
      <Grid container spacing={3}>
        {campaigns.map(campaign => (
          <Grid
            key={campaign.campaign_id}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <Card>
              <CardActionArea onClick={() => handleCampaignClick(campaign.campaign_id)}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" component="div">
                      {campaign.name}
                    </Typography>
                    <StatusChip status={campaign.status ?? 'disabled'} />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace', mb: 1 }}
                  >
                    ID: {campaign.campaign_id}
                    <CopyButton text={campaign.campaign_id} tooltip="Copy Campaign ID" />
                  </Typography>
                  <Typography variant="body2">
                    {/* The data_collected_seconds property is now part of the status data that gets merged 
                        by the API into the campaign object during list retrieval */}
                    Data Collected:{' '}
                    {campaign.data_collected_seconds !== null &&
                    campaign.data_collected_seconds !== undefined
                      ? formatSecondsToHMS(campaign.data_collected_seconds)
                      : 'None'}
                  </Typography>
                  {campaign.last_updated && (
                    <Typography variant="body2" color="text.secondary">
                      Last Updated: {new Date(campaign.last_updated).toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default Dashboard;
